/**
 * WB3-1 + WB3-P2-2 — Role and variant default intent presets
 * @see BUSINESS_WORKBENCH_NORTH_STAR_V3.md §4 · §8
 */

import type { UserRole } from '@shared/types/roles';
import { ROLES } from '@shared/types/roles';
import type { WorkbenchVariant } from './workbench-focus';
import type {
  AccessIntent,
  DocumentIntent,
  OperationalIntent,
  RelationshipIntent,
  SiteIntent,
  WorkbenchIntent,
} from './workbench-intent';
import {
  isAccessIntent,
  isDocumentIntent,
  isRelationshipIntent,
  isSiteIntent,
} from './workbench-intent';

export type RoleIntentPreset = 'finance' | 'sales' | 'warehouse';

/** Settings-facing role preset → default operational intent (v1 static map). */
export const ROLE_INTENT_PRESETS = {
  finance: 'collect_payment',
  sales: 'process_new_order',
  warehouse: 'receive_inventory',
} as const satisfies Record<RoleIntentPreset, OperationalIntent>;

const USER_ROLE_PRESET_MAP: Partial<Record<UserRole, RoleIntentPreset>> = {
  [ROLES.ACCOUNTING]: 'finance',
  [ROLES.SALES]: 'sales',
  [ROLES.OPERATIONS_MANAGER]: 'warehouse',
};

/** Variant-scoped namespace defaults when domain hints are neutral. */
export const VARIANT_INTENT_PRESETS: Readonly<Partial<Record<WorkbenchVariant, WorkbenchIntent>>> =
  {
    site: 'edit_site_page',
    document: 'preview_document',
  };

/** Role → namespace intent when variant/domain do not disambiguate. */
export const ROLE_NAMESPACE_INTENT_PRESETS: Partial<Record<UserRole, WorkbenchIntent>> = {
  [ROLES.OWNER]: 'review_permissions',
  [ROLES.HR_ADMIN]: 'assign_role',
  [ROLES.EXTERNAL]: 'review_relationship',
};

/** Domain-specific namespace hints (object variant). */
export const DOMAIN_NAMESPACE_INTENT_PRESETS: Readonly<Record<string, WorkbenchIntent>> = {
  internal_entity: 'review_permissions',
  external_entity: 'review_relationship',
  evidence_document: 'edit_template',
  org_document: 'preview_document',
};

/** Maps tenant UserRole to Finance/Sales/Warehouse preset when applicable. */
export function resolveRoleIntentPreset(role: UserRole): RoleIntentPreset | undefined {
  return USER_ROLE_PRESET_MAP[role];
}

export function resolvePresetIntent(
  preset: RoleIntentPreset
): (typeof ROLE_INTENT_PRESETS)[RoleIntentPreset] {
  return ROLE_INTENT_PRESETS[preset];
}

export function resolveVariantIntentPreset(variant: WorkbenchVariant): WorkbenchIntent | undefined {
  return VARIANT_INTENT_PRESETS[variant];
}

export function resolveDomainNamespaceIntent(domain: string): WorkbenchIntent | undefined {
  const normalized = domain.trim().toLowerCase();
  if (normalized.length === 0) {
    return undefined;
  }

  const direct = DOMAIN_NAMESPACE_INTENT_PRESETS[normalized];
  if (direct !== undefined) {
    return direct;
  }

  for (const [key, intent] of Object.entries(DOMAIN_NAMESPACE_INTENT_PRESETS)) {
    if (normalized.includes(key)) {
      return intent;
    }
  }

  return undefined;
}

/** Role + variant namespace preset (site/document/access/relationship). */
export function resolveNamespaceIntentPreset(
  role: UserRole,
  variant?: WorkbenchVariant
): WorkbenchIntent | undefined {
  if (variant !== undefined) {
    return resolveVariantIntentPreset(variant);
  }

  return ROLE_NAMESPACE_INTENT_PRESETS[role];
}

export type NamespaceIntentPreset = SiteIntent | DocumentIntent | AccessIntent | RelationshipIntent;

export function isNamespaceIntent(intent: WorkbenchIntent): intent is NamespaceIntentPreset {
  return (
    isSiteIntent(intent) ||
    isDocumentIntent(intent) ||
    isAccessIntent(intent) ||
    isRelationshipIntent(intent)
  );
}
